import { useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface UseIdleTimeoutOptions {
  timeout?: number; // timeout in milliseconds
  onIdle?: () => void;
  onActive?: () => void;
  onAction?: () => void;
  events?: string[];
  element?: Document | Element;
  startOnMount?: boolean;
  stopOnUnmount?: boolean;
}

const DEFAULT_EVENTS = [
  'mousedown',
  'mousemove',
  'keypress',
  'scroll',
  'touchstart',
  'click',
];

const ONE_HOUR = 60 * 60 * 1000; // 1 hour in milliseconds
const WARNING_TIME = 5 * 60 * 1000; // Show warning 5 minutes before logout

export const useIdleTimeout = ({
  timeout = ONE_HOUR,
  onIdle,
  onActive,
  onAction,
  events = DEFAULT_EVENTS,
  element = document,
  startOnMount = true,
  stopOnUnmount = true,
}: UseIdleTimeoutOptions = {}) => {
  const timeoutId = useRef<NodeJS.Timeout>();
  const warningTimeoutId = useRef<NodeJS.Timeout>();
  const isIdle = useRef(false);
  const isWarningShown = useRef(false);

  const reset = useCallback(() => {
    // Clear existing timeouts
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
    }
    if (warningTimeoutId.current) {
      clearTimeout(warningTimeoutId.current);
    }

    // Reset warning state
    isWarningShown.current = false;

    // If user was idle, call onActive
    if (isIdle.current) {
      isIdle.current = false;
      onActive?.();
    }

    // Set warning timeout (5 minutes before actual logout)
    warningTimeoutId.current = setTimeout(() => {
      if (!isWarningShown.current) {
        isWarningShown.current = true;
        toast.warning('Session Expiring', {
          description: 'You will be logged out in 5 minutes due to inactivity. Move your mouse or click to stay logged in.',
          duration: 10000, // Show for 10 seconds
        });
      }
    }, timeout - WARNING_TIME);

    // Set idle timeout
    timeoutId.current = setTimeout(() => {
      isIdle.current = true;
      handleIdle();
    }, timeout);
  }, [timeout, onActive, onIdle]);

  const handleIdle = useCallback(async () => {
    // Auto logout the user
    try {
      await supabase.auth.signOut();
      toast.error('Session Expired', {
        description: 'You have been logged out due to inactivity.',
        duration: 5000,
      });
      onIdle?.();
    } catch (error) {
      console.error('Error during auto logout:', error);
    }
  }, [onIdle]);

  const handleAction = useCallback(() => {
    onAction?.();
    reset();
  }, [onAction, reset]);

  const start = useCallback(() => {
    // Add event listeners
    events.forEach((event) => {
      element.addEventListener(event, handleAction, true);
    });

    // Start the timer
    reset();
  }, [events, element, handleAction, reset]);

  const stop = useCallback(() => {
    // Remove event listeners
    events.forEach((event) => {
      element.removeEventListener(event, handleAction, true);
    });

    // Clear timeouts
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
    }
    if (warningTimeoutId.current) {
      clearTimeout(warningTimeoutId.current);
    }

    // Reset state
    isIdle.current = false;
    isWarningShown.current = false;
  }, [events, element, handleAction]);

  useEffect(() => {
    if (startOnMount) {
      start();
    }

    return () => {
      if (stopOnUnmount) {
        stop();
      }
    };
  }, [start, stop, startOnMount, stopOnUnmount]);

  return {
    isIdle: isIdle.current,
    start,
    stop,
    reset,
  };
};

export default useIdleTimeout;